import * as React from "react"
import { useEffect, useCallback } from "react"
import { Dialog, Box, Typography, Avatar, IconButton, Button, Chip, Tooltip } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { postSubmit } from "@ui-seeker/utils"
import { useAppToast } from "@ui-seeker/app"

import { BACKEND_APIS } from "@constants"
import { matchCompareModal, tableData } from "../@signals"


function ProductCard({ product, label, labelColor }) {
    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, gap: 1.5 }}>
            <Chip label={label} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: labelColor, color: 'white', mb: 1 }} />
            <Avatar
                src={product?.image_url}
                variant="rounded"
                sx={{ width: 180, height: 180, bgcolor: '#f3f4f6', '& img': { objectFit: 'contain' } }}
            >
                <IconSeeker icon="image" sx={{ color: '#d1d5db', fontSize: 40 }} />
            </Avatar>
            <Tooltip title={product?.name || '-'} placement="bottom">
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', lineHeight: 1.4 }}>
                    {product?.name || product?.product_id || '-'}
                </Typography>
            </Tooltip>
            {product?.variant_raw && (
                <Typography component="span" variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, bgcolor: '#e0e7ff', color: '#4338ca', px: 0.75, py: 0.15, borderRadius: 0.5 }}>
                    {product.variant_raw}
                </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                {product?.site_name && <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 500, fontSize: '0.65rem' }}>{product.site_name}</Typography>}
                <Typography variant="caption" sx={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.6rem' }}>{product?.product_id}</Typography>
            </Box>
            {product?.product_url && (
                <a href={product.product_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Typography variant="caption" sx={{ color: '#6366f1', '&:hover': { textDecoration: 'underline' } }}>View product</Typography>
                    <IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 10, color: '#6366f1' }} />
                </a>
            )}
        </Box>
    )
}


export default function MatchCompareModal() {
    const { showToast } = useAppToast()
    const modal = matchCompareModal.value
    const open = !!modal
    const product = modal?.product
    const matches = modal?.matches || []
    const index = modal?.index || 0
    const currentMatch = matches[index]

    const handleClose = () => matchCompareModal.value = null

    const handlePrev = useCallback(() => {
        if (!modal || index <= 0) return
        matchCompareModal.value = { ...modal, index: index - 1 }
    }, [modal, index])

    const handleNext = useCallback(() => {
        if (!modal || index >= matches.length - 1) return
        matchCompareModal.value = { ...modal, index: index + 1 }
    }, [modal, index, matches.length])

    const handleUnmap = useCallback(() => {
        if (!currentMatch || !product) return

        // Optimistic update
        const newMatches = matches.filter((_, i) => i !== index)
        tableData.value = tableData.value.map(item =>
            item.id_site === product.id_site && item.product_id === product.product_id
                ? { ...item, matches: (item.matches || []).filter(m => !(m.id_site === currentMatch.id_site && m.product_id === currentMatch.product_id)) }
                : item
        )

        // API call
        postSubmit(`${BACKEND_APIS.RETAILER_PRODUCTS}/unmap`, {
            id_customer: product.id_customer,
            id_site: product.id_site,
            product_id: product.product_id,
            target_id_site: currentMatch.id_site,
            target_product_id: currentMatch.product_id
        }).catch(err => console.error('Error unmapping:', err))

        showToast.success('Match removed')

        // Navigate or close
        if (newMatches.length === 0) {
            matchCompareModal.value = null
        } else {
            const newIndex = index >= newMatches.length ? newMatches.length - 1 : index
            matchCompareModal.value = { ...modal, matches: newMatches, index: newIndex }
        }
    }, [modal, product, currentMatch, matches, index])

    // Keyboard navigation
    useEffect(() => {
        if (!open) return
        const handleKey = (e) => {
            if (e.key === 'ArrowLeft') handlePrev()
            else if (e.key === 'ArrowRight') handleNext()
        }
        window.addEventListener('keydown', handleKey)
        return () => window.removeEventListener('keydown', handleKey)
    }, [open, handlePrev, handleNext])

    if (!open || !currentMatch) return null

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Compare match
                </Typography>
                <IconButton size="small" onClick={handleClose}>
                    <IconSeeker icon="xmark" sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            {/* Product comparison */}
            <Box sx={{ display: 'flex', alignItems: 'stretch', minHeight: 340 }}>
                <ProductCard product={product} label="Original" labelColor="#3b82f6" />

                {/* Divider + VS */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 1 }}>
                    <Box sx={{ width: 1, flex: 1, bgcolor: '#e5e7eb' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#9ca3af', py: 1 }}>VS</Typography>
                    <Box sx={{ width: 1, flex: 1, bgcolor: '#e5e7eb' }} />
                </Box>

                <ProductCard product={currentMatch} label="Match" labelColor="#10b981" />
            </Box>

            {/* Navigation + Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2, borderTop: '1px solid #e5e7eb' }}>
                {/* Nav arrows */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton size="small" onClick={handlePrev} disabled={index <= 0} sx={{ border: '1px solid #e5e7eb' }}>
                        <IconSeeker icon="chevron-left" sx={{ fontSize: 14 }} />
                    </IconButton>
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, minWidth: 40, textAlign: 'center' }}>
                        {index + 1} / {matches.length}
                    </Typography>
                    <IconButton size="small" onClick={handleNext} disabled={index >= matches.length - 1} sx={{ border: '1px solid #e5e7eb' }}>
                        <IconSeeker icon="chevron-right" sx={{ fontSize: 14 }} />
                    </IconButton>
                </Box>

                {/* Unmap button */}
                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleUnmap}
                    startIcon={<IconSeeker icon="link-slash" sx={{ fontSize: 14 }} />}
                >
                    Remove match
                </Button>
            </Box>
        </Dialog>
    )
}
